import {
  getGetInvoiceCompaniesQueryKey,
  useDeleteInvoiceCompaniesId,
  useGetInvoiceCompanies,
  usePatchInvoiceCompaniesId,
  usePostInvoiceCompanies,
} from '@agos/api-client/invoicing'
import { T, tt } from '@agos/shared'
import { ConfirmDialog, DataTable, PageHeader } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Building2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  companiesOf,
  companyFormOf,
  companyLine,
  companyPayload,
  companyProblem,
  emptyCompanyForm,
  matchingCompanies,
  type CompanyForm,
  type InvoiceCompany,
} from './invoice-companies'

/**
 * *Externé Firmy → Firmy* - the companies this restaurant invoices, FRONTEND.md §9.347.
 *
 * **The sell side.** *Dodávatelia* is who the kitchen buys from; this is who it invoices, and they
 * are different organisations - which is why §10A.135 gave them their own table rather than a flag on
 * `suppliers`, a table legacy reads unfiltered.
 *
 * **Disabled rather than deleted, by default.** A company switched off leaves the invoice form's
 * dropdown and stays here, because the usual reason to remove one is *we do not invoice them any
 * more* rather than *this row was a mistake*. Deleting is offered too, and it is safe: the six
 * recipient fields live on each issued invoice, so nothing it can reach is a document.
 */
export function CompaniesPage() {
  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Firmy')}
        description={tt('Firmy, ktorým reštaurácia fakturuje - vyberú sa pri vydanej faktúre')}
        icon={<Building2 className="w-5 h-5" />}
      />
      <CompaniesBody />
    </div>
  )
}

/** The list without a header, so the hub can draw one of its own - §9.302's shape. */
export function CompaniesBody() {
  const queryClient = useQueryClient()
  // `includeDisabled`, because this is the only screen that can switch one back on.
  const { data, isLoading } = useGetInvoiceCompanies({ includeDisabled: true })
  const companies = useMemo(() => companiesOf(data), [data])
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<{ id: number | null; form: CompanyForm } | null>(null)
  const [deleting, setDeleting] = useState<InvoiceCompany | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: getGetInvoiceCompaniesQueryKey() })
  const create = usePostInvoiceCompanies({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        refresh()
      },
    },
  })
  const update = usePatchInvoiceCompaniesId({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        refresh()
      },
    },
  })
  const remove = useDeleteInvoiceCompaniesId({
    mutation: {
      onSuccess: () => {
        setDeleting(null)
        refresh()
      },
    },
  })

  const shown = useMemo(() => matchingCompanies(companies, search), [companies, search])

  const save = (draft: { id: number | null; form: CompanyForm }) => {
    const refusal = companyProblem(draft.form)
    setProblem(refusal)
    if (refusal !== null) return
    const data = companyPayload(draft.form)
    if (draft.id === null) create.mutate({ data })
    else update.mutate({ id: draft.id, data })
  }

  return (
    <div className="space-y-3" data-invoice-companies={companies.length}>
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={tt('Hľadať firmu, IČO alebo adresu...')}
          aria-label={tt('Hľadať firmu')}
          data-company-search
          className="flex-1 min-w-[200px] min-h-[36px] rounded-xl border border-border bg-background px-3 text-sm"
        />
        <button
          type="button"
          data-company-add
          onClick={() => {
            setProblem(null)
            setEditing({ id: null, form: emptyCompanyForm() })
          }}
          className="min-h-[36px] px-4 rounded-xl border border-primary/40 bg-primary/10 text-primary text-sm font-semibold"
        >
          <T>Pridať firmu</T>
        </button>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">
          <T>Načítava sa...</T>
        </p>
      ) : (
        <DataTable<InvoiceCompany>
          columns={[
            {
              key: 'name',
              header: tt('Firma'),
              render: (company) => (
                <div>
                  <p className={company.enabled ? 'font-semibold' : 'font-semibold opacity-50'}>
                    {company.name}
                    {!company.enabled && <> · {tt('vypnutá')}</>}
                  </p>
                  <p className="text-[11px] text-muted-foreground">{companyLine(company)}</p>
                </div>
              ),
            },
            { key: 'dic', header: tt('DIČ'), render: (company) => company.dic ?? '-' },
            { key: 'icDph', header: tt('IČ DPH'), render: (company) => company.icDph ?? '-' },
            { key: 'email', header: tt('E-mail'), render: (company) => company.email ?? '-' },
            {
              key: 'actions',
              header: '',
              render: (company) => (
                <div className="flex gap-1 justify-end">
                  <button
                    type="button"
                    data-company-edit={String(company.id)}
                    onClick={() => {
                      setProblem(null)
                      setEditing({ id: company.id, form: companyFormOf(company) })
                    }}
                    className="px-2 py-1 rounded-lg border border-border text-[11px] font-semibold"
                  >
                    <T>Upraviť</T>
                  </button>
                  <button
                    type="button"
                    data-company-delete={String(company.id)}
                    onClick={() => setDeleting(company)}
                    className="px-2 py-1 rounded-lg border border-border text-[11px] font-semibold text-destructive"
                  >
                    <T>Zmazať</T>
                  </button>
                </div>
              ),
            },
          ]}
          data={shown}
          rowKey={(company) => company.id}
          pageSize={25}
          compact
          emptyMessage={tt('Žiadne firmy - pridajte prvú, alebo ju vyberte pri vydanej faktúre')}
          emptyIcon={<Building2 className="w-10 h-10 mb-3 opacity-30" />}
        />
      )}

      {editing !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={editing.id === null ? tt('Pridať firmu') : tt('Upraviť firmu')}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-company-editor
        >
          <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-4 space-y-2">
            {(
              [
                ['name', tt('Názov firmy')],
                ['address', tt('Adresa')],
                ['ico', tt('IČO')],
                ['dic', tt('DIČ')],
                ['icDph', tt('IČ DPH')],
                ['email', tt('E-mail')],
                ['notes', tt('Poznámka')],
              ] as const
            ).map(([field, label]) => (
              <label key={field} className="block space-y-1">
                <span className="text-[11px] text-muted-foreground">{label}</span>
                <input
                  value={editing.form[field]}
                  data-company-field={field}
                  onChange={(event) =>
                    setEditing({ ...editing, form: { ...editing.form, [field]: event.target.value } })
                  }
                  className="w-full min-h-[34px] rounded-lg border border-border bg-background px-2 text-sm"
                />
              </label>
            ))}
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                data-company-enabled
                checked={editing.form.enabled}
                onChange={(event) =>
                  setEditing({ ...editing, form: { ...editing.form, enabled: event.target.checked } })
                }
              />
              <T>Ponúkať pri vydanej faktúre</T>
            </label>
            {problem !== null && (
              <p className="text-[11px] text-destructive" data-company-problem>
                {problem}
              </p>
            )}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                data-company-cancel
                onClick={() => setEditing(null)}
                className="flex-1 min-h-[36px] rounded-xl border border-border text-xs font-semibold"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="button"
                data-company-save
                disabled={create.isPending || update.isPending}
                onClick={() => save(editing)}
                className="flex-1 min-h-[36px] rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
              >
                <T>Uložiť</T>
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting !== null && remove.mutate({ id: deleting.id })}
        title={tt('Zmazať firmu?')}
        // Said plainly, because it is the question somebody actually has: the invoices keep their own
        // copy of the recipient, so this removes a row from a list and nothing else - §10A.135.
        description={`${deleting?.name ?? ''} - ${tt('vydané faktúry sa nezmenia, majú vlastnú kópiu údajov. Zmizne len z ponuky.')}`}
      />
    </div>
  )
}
